/**
 * ResponsibleGambling — R1 rewrite.
 * Preserves backend contract 1:1:
 *   GET  /player/rg-settings
 *   POST /player/rg-settings  {daily_limit,weekly_limit,monthly_limit,session_alert_minutes}
 *   POST /player/rg-settings  {self_exclude}
 */
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast, Toaster } from "sonner";
import { AlertTriangle, Clock, X } from "lucide-react";

import api from "../lib/api";
import { extractError } from "../lib/formatError";
import { useAuth } from "../context/AuthContext";
import AppShell from "../components/ui-r1/AppShell";
import Button from "../components/ui-r1/Button";
import Input from "../components/ui-r1/Input";
import { Card, CardBody } from "../components/ui-r1/Card";
import { Badge } from "../components/ui-r1/Badge";
import ChatWidget from "../components/ChatWidget";

export default function ResponsibleGambling() {
  const { refresh } = useAuth();
  const [state, setState] = useState(null);
  const [daily, setDaily] = useState("");
  const [weekly, setWeekly] = useState("");
  const [monthly, setMonthly] = useState("");
  const [alertMin, setAlertMin] = useState("60");
  const [selfExc, setSelfExc] = useState("");
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirmExcl, setConfirmExcl] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/player/rg-settings");
      setState(data);
      setDaily(String(data.daily_limit_usd || 0));
      setWeekly(String(data.weekly_limit_usd || 0));
      setMonthly(String(data.monthly_limit_usd || 0));
      setAlertMin(String(data.session_alert_minutes || 60));
    } catch (e) { setError(extractError(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const saveLimits = async (e) => {
    e.preventDefault();
    setBusy(true); setError(null);
    try {
      await api.post("/player/rg-settings", {
        daily_limit: Number(daily) || 0,
        weekly_limit: Number(weekly) || 0,
        monthly_limit: Number(monthly) || 0,
        session_alert_minutes: Number(alertMin) || 0,
      });
      toast.success("Preferences saved");
      await load();
    } catch (e2) { setError(extractError(e2)); } finally { setBusy(false); }
  };

  const submitExclusion = async () => {
    setConfirmExcl(false); setBusy(true); setError(null);
    try {
      await api.post("/player/rg-settings", { self_exclude: selfExc });
      toast.success("Self-exclusion applied");
      await load(); await refresh();
    } catch (e) { setError(extractError(e)); } finally { setBusy(false); setSelfExc(""); }
  };

  if (!state) {
    return (
      <AppShell activeSection="settings" pageTitle="Responsible gambling">
        <div className="max-w-3xl mx-auto px-8 py-10 text-ink-2">Loading…</div>
      </AppShell>
    );
  }

  const currentExcl = state.self_excluded_until ? new Date(state.self_excluded_until) : null;
  const allowedPeriods = state.policy.allowed_self_exclusion_periods || [];

  return (
    <AppShell activeSection="settings" pageTitle="Responsible gambling">
      <Toaster theme="dark" position="top-center" />

      <div className="max-w-3xl mx-auto px-4 sm:px-8 py-6 space-y-6" data-testid="rg-page">
        <div>
          <div className="text-[10px] tracking-[0.4em] uppercase text-pink mb-2">Settings</div>
          <h1 className="font-display italic text-3xl sm:text-4xl text-ink">Responsible gambling</h1>
          <p className="text-sm text-ink-2 mt-2 max-w-xl">
            Set your own limits and self-exclusion. All limits are enforced server-side on every deposit.
          </p>
        </div>

        {error && (
          <div className="text-xs text-danger border border-danger/60 bg-danger/10 rounded-lg px-3 py-2" data-testid="rg-error">{error}</div>
        )}

        <form onSubmit={saveLimits} className="space-y-4" data-testid="rg-form">
          {/* Deposit limits */}
          <Card>
            <CardBody>
              <div className="text-[10px] tracking-[0.25em] uppercase text-ink-2 mb-3">Deposit limits (USD)</div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <Input type="number" min="0" step="1" label="Daily" value={daily} onChange={(e) => setDaily(e.target.value)} data-testid="rg-daily" inputClassName="font-mono" />
                <Input type="number" min="0" step="1" label="Weekly" value={weekly} onChange={(e) => setWeekly(e.target.value)} data-testid="rg-weekly" inputClassName="font-mono" />
                <Input type="number" min="0" step="1" label="Monthly" value={monthly} onChange={(e) => setMonthly(e.target.value)} data-testid="rg-monthly" inputClassName="font-mono" />
              </div>
              <div className="text-[10px] tracking-widest uppercase text-ink-3 mt-3">
                Lowering a limit takes effect immediately. Raising takes {state.policy.cooldown_hours}h.
              </div>
            </CardBody>
          </Card>

          {/* Session alert */}
          <Card>
            <CardBody>
              <div className="text-[10px] tracking-[0.25em] uppercase text-ink-2 mb-3">Session-time alert</div>
              <select
                value={alertMin}
                onChange={(e) => setAlertMin(e.target.value)}
                className="w-full sm:w-72 bg-base border border-line rounded-lg px-3 py-2.5 text-sm text-ink focus:border-pink focus:outline-none focus:shadow-glow-pink transition"
                data-testid="rg-session-alert"
              >
                {[15, 30, 60, 90, 120].map((m) => <option key={m} value={m}>{`Every ${m} minutes`}</option>)}
              </select>
            </CardBody>
          </Card>

          {/* Pending increases */}
          {state.pending_increases.length > 0 && (
            <Card>
              <CardBody>
                <div className="text-[10px] tracking-[0.25em] uppercase text-ink-2 mb-3">Pending limit increases</div>
                <ul className="text-xs font-mono text-ink-2 space-y-2">
                  {state.pending_increases.map((it) => (
                    <li key={it.id} className="p-3 rounded-lg bg-highest border border-line" data-testid={`pending-${it.id}`}>
                      Effective <span className="text-ink">{new Date(it.effective_at).toLocaleString()}</span>:
                      {it.new_daily != null && ` daily $${it.new_daily}`}
                      {it.new_weekly != null && ` · weekly $${it.new_weekly}`}
                      {it.new_monthly != null && ` · monthly $${it.new_monthly}`}
                      <Countdown to={it.effective_at} />
                    </li>
                  ))}
                </ul>
              </CardBody>
            </Card>
          )}

          <Button type="submit" disabled={busy} loading={busy} variant="primary" size="lg" className="w-full sm:w-auto px-10" data-testid="rg-save-btn">
            {busy ? "Saving…" : "Save preferences"}
          </Button>
        </form>

        {/* Self-exclusion — danger zone */}
        <Card className="border-danger/50" data-testid="rg-self-exclusion">
          <CardBody>
            <div className="flex items-center gap-2 mb-2">
              <AlertTriangle className="w-4 h-4 text-danger" />
              <Badge variant="danger">Danger zone · self-exclusion</Badge>
            </div>
            <p className="text-sm text-ink-2 mb-4">
              Blocks all logins and bets for the chosen period. <b className="text-ink">This cannot be reversed</b>, only extended.
            </p>
            {currentExcl ? (
              <div className="text-sm text-ink mb-3 p-3 rounded-lg bg-danger/10 border border-danger/40">
                Currently self-excluded until <span className="font-mono">{currentExcl.getFullYear() > 9000 ? "permanently" : currentExcl.toLocaleString()}</span>.
              </div>
            ) : (
              <div className="text-xs text-ink-3 mb-3 italic">Not active.</div>
            )}
            <div className="flex flex-wrap gap-2 items-center">
              <select
                value={selfExc}
                onChange={(e) => setSelfExc(e.target.value)}
                className="bg-base border border-line rounded-lg px-3 py-2.5 text-sm text-ink focus:border-danger focus:outline-none w-full sm:w-64 transition"
                data-testid="rg-exclusion-period"
              >
                <option value="">Select period…</option>
                {allowedPeriods.map((p) => <option key={p} value={p}>{p === "permanent" ? "Permanent" : p}</option>)}
              </select>
              <Button
                onClick={() => selfExc && setConfirmExcl(true)}
                disabled={!selfExc || busy}
                variant="danger"
                size="lg"
                data-testid="rg-exclude-btn"
              >
                Self-exclude
              </Button>
            </div>
          </CardBody>
        </Card>
      </div>

      {confirmExcl && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center px-4"
          style={{ background: "rgba(0,0,0,0.75)", backdropFilter: "blur(6px)" }}
          data-testid="rg-confirm-modal"
        >
          <Card className="w-full max-w-md border-danger/60">
            <CardBody>
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-danger" />
                  <div className="font-display italic text-xl text-danger">Confirm self-exclusion</div>
                </div>
                <button onClick={() => setConfirmExcl(false)} className="text-ink-3 hover:text-ink" aria-label="Close">
                  <X className="w-4 h-4" />
                </button>
              </div>
              <p className="text-sm text-ink mb-2">
                You are about to exclude yourself for <b>{selfExc}</b>.
              </p>
              <p className="text-xs text-ink-2 mb-4">
                This is <b className="text-danger">irreversible</b>. Not even support can shorten it — only extend.
              </p>
              <div className="flex gap-2">
                <Button onClick={() => setConfirmExcl(false)} variant="ghost" size="md" className="flex-1">Cancel</Button>
                <Button onClick={submitExclusion} variant="danger" size="md" className="flex-1" data-testid="rg-confirm-exclude">
                  I understand — exclude me
                </Button>
              </div>
            </CardBody>
          </Card>
        </div>
      )}

      <ChatWidget />
    </AppShell>
  );
}

function Countdown({ to }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => { const i = setInterval(() => setNow(Date.now()), 60000); return () => clearInterval(i); }, []);
  const ms = new Date(to).getTime() - now;
  if (ms <= 0) return <span className="ml-2 text-gold">· ready</span>;
  const h = Math.floor(ms / 3600000);
  const m = Math.floor((ms % 3600000) / 60000);
  return <span className="ml-2 text-ink-3"><Clock className="w-2.5 h-2.5 inline" /> {h}h {m}m</span>;
}
